import { sharePointInternalFileName, sharePointInternalTitleName } from "../../config/constants";
import { FileOption } from "../../interfaces/IFileOption";
import { IFileToUpload } from "../../interfaces/IFileToUpload";
import { SharePointInputData } from "../../interfaces/props/SharePointInputData";
import emailMapper from "../../services/EmailMapper";
import { DefaultValueService } from "../../services/DefaultValueService";
import { selectedExchangeItemsStore } from "../../services/selectedExchangeItemsStore";
import { LocalizationModule } from "../../../../utilities/LocalizationModule";

// Single resolution rule for email-mapped columns, shared by every per-file
// save/build path (individual-properties panel, Outlook and content-manager
// persists, re-checked files). Kept out of metaDataHelper so that module stays
// free of environment-dependent imports (logger/auth chains) for unit tests.
//
// - Column not email-mapped: the current value is kept as-is.
// - Mapped and the email has a value: the value resolved from that email wins
//   (the pre-existing overwrite semantics of the copy-emails flow).
// - Mapped and the email has NO value for the property: only the auto-set
//   placeholder is cleared — it must never surface or persist as a real
//   SharePoint column value. Any other current value is kept: in the
//   single-cached-email flows the mapped fields are editable, so the current
//   value can be user input or a folder default, which clearing would destroy.
export const resolveEmailMappedInputValue = (inputData: SharePointInputData, currentValue: any, selectedItem: any): any => {
    const displayName: string = inputData?.xml?.DisplayName;
    if (!displayName) {
        return currentValue;
    }

    const colName = displayName.trim().toLowerCase();
    const spColumnName = emailMapper.getAllListedColumns().find(col => col.toLowerCase() === colName);
    if (!spColumnName) {
        return currentValue;
    }

    if (selectedItem) {
        const mappedResult = emailMapper.getOfficeJSToEmailMapping(spColumnName, selectedItem);
        if (mappedResult.mappedVal?.value) {
            return DefaultValueService.setMappedDefault(inputData, mappedResult.mappedVal);
        }
    }

    if (LocalizationModule.isAutoSetEmailPropertyPlaceholder(currentValue)) {
        return null;
    }

    return currentValue;
};

export const getSelectedExchangeItem = (graphMailItemId: string): any => {
    if (!graphMailItemId) {
        return null;
    }
    const selectedItems = selectedExchangeItemsStore.get();
    return selectedItems?.find(x => x.id === graphMailItemId) ?? null;
};

// Waits for an in-flight header load instead of misreading the transient
// empty cache as "nothing mapped" — the metadata form reads the cache exactly
// once when it is built, so a read that races the load would leave every
// email-mapped column permanently empty. See selectedExchangeItemsStore.
export const getSelectedExchangeItemsAsync = (): Promise<any[]> => {
    return selectedExchangeItemsStore.getAsync();
};

// Re-resolves the email-mapped columns of the file option fileCheckedToggled
// (re)creates for a re-checked file. That option is built from the shared form,
// which for email-mapped columns only carries the auto-set placeholder — the
// file must get its own mapped header values back (or a cleared field).
export const applyEmailMappingToToggledFileOption = (fileOptions: FileOption[], toggledFile: IFileToUpload): FileOption[] => {
    if (!Array.isArray(fileOptions) || !toggledFile) {
        return fileOptions;
    }

    const selectedItem = getSelectedExchangeItem(toggledFile.graphMailItemId);

    return fileOptions.map(fileOption => {
        if (fileOption.key !== toggledFile.id || !Array.isArray(fileOption.fileInputValues)) {
            return fileOption;
        }

        return {
            ...fileOption,
            fileInputValues: fileOption.fileInputValues.map(inputValue => {
                // Name/Title stay on the file name fileCheckedToggled assigned.
                if (inputValue.internalName === sharePointInternalFileName || inputValue.internalName === sharePointInternalTitleName) {
                    return inputValue;
                }

                const value = resolveEmailMappedInputValue(inputValue, inputValue.value, selectedItem);
                return value === inputValue.value ? inputValue : { ...inputValue, value };
            })
        };
    });
};
